import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AlertTriangle, Clock, Send } from 'lucide-react';

import { DeclarationNouvelleForm } from './DeclarationNouvelleForm';
import { DeclarationSuiviList } from './DeclarationSuiviList';
import { DeclarationDetailsModal } from './DeclarationDetailsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeclarationDommage({ subscriptionReference, onDeclarationCreated }) {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('nouvelle');
  const [loading, setLoading] = useState(false);
  const [mesDeclarations, setMesDeclarations] = useState([]);
  const [souscriptions, setSouscriptions] = useState([]);
  const [selectedDeclaration, setSelectedDeclaration] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    subscription_reference: subscriptionReference || '',
    nom: user?.nom || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    date_decouverte: '',
    type_dommage: '',
    description: '',
    localisation: '',
    evolution: '',
    urgence: false,
    certifie: false
  });
  
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    if (token) {
      fetchMesDeclarations();
      fetchMesSouscriptions();
    }
  }, [token]);

  useEffect(() => {
    if (subscriptionReference) {
      setFormData(prev => ({ ...prev, subscription_reference: subscriptionReference }));
    }
  }, [subscriptionReference]);

  const fetchMesDeclarations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/domium/dommages/mes-declarations/liste`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMesDeclarations(data.declarations || []);
      }
    } catch {
    }
  };

  const fetchMesSouscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/domium/subscribe/my-simulations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filtrer uniquement les souscriptions actives
        const activeSubscriptions = (data.simulations || []).filter(s => 
          ['ACTIVE', 'PAID_CONFIRMED', 'ATTESTATION_PROVISOIRE', 'ATTESTATION_DEFINITIVE'].includes(s.status)
        );
        setSouscriptions(activeSubscriptions);
      }
    } catch {
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValid = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isSmallEnough = file.size <= 10 * 1024 * 1024; // 10MB
      return isValid && isSmallEnough;
    });
    
    if (validFiles.length !== selectedFiles.length) {
      toast.warning('Certains fichiers ont été ignorés (type non supporté ou taille > 10MB)');
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.certifie) {
      toast.error('Vous devez certifier l\'exactitude des informations');
      return;
    }
    
    if (!formData.subscription_reference) {
      toast.error('Veuillez sélectionner une souscription DOMIUM');
      return;
    }
    
    if (!formData.date_decouverte) {
      toast.error('Veuillez indiquer la date de découverte des désordres');
      return;
    }
    
    setLoading(true);
    
    try {
      // 1. Créer la déclaration
      const response = await fetch(`${API_URL}/api/domium/dommages/declarer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création');
      }
      
      const result = await response.json();
      const reference = result.declaration.reference;
      
      toast.success(`Déclaration créée: ${reference}`);
      
      // 2. Upload des fichiers
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formDataFile = new FormData();
          formDataFile.append('file', file);
          formDataFile.append('doc_type', file.type.startsWith('image/') ? 'photo' : 'document');
          formDataFile.append('description', `Document ${i + 1}`);
          
          try {
            setUploadProgress(prev => ({ ...prev, [i]: 'uploading' }));
            
            const uploadResponse = await fetch(
              `${API_URL}/api/domium/dommages/${reference}/documents`,
              {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formDataFile
              }
            );
            
            if (uploadResponse.ok) {
              setUploadProgress(prev => ({ ...prev, [i]: 'success' }));
            } else {
              setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
            }
          } catch {
            setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
          }
        }
      }
      
      // Reset form
      setFormData({
        subscription_reference: subscriptionReference || '',
        nom: user?.nom || '',
        email: user?.email || '',
        telephone: user?.telephone || '',
        date_decouverte: '',
        type_dommage: '',
        description: '',
        localisation: '',
        evolution: '',
        urgence: false,
        certifie: false
      });
      setFiles([]);
      setUploadProgress({});
      
      // Refresh list
      fetchMesDeclarations();
      setActiveTab('suivi');
      
      if (onDeclarationCreated) {
        onDeclarationCreated(result.declaration);
      }
      
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la déclaration');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (reference) => {
    try {
      const response = await fetch(`${API_URL}/api/domium/dommages/${reference}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Declaration_Dommage_${reference}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Erreur téléchargement PDF');
    }
  };

  const viewDeclarationDetails = async (reference) => {
    try {
      const response = await fetch(`${API_URL}/api/domium/dommages/${reference}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedDeclaration(data);
      }
    } catch {
      toast.error('Erreur chargement détails');
    }
  };

  return (
    <div className="space-y-6" data-testid="declaration-dommage-container">
      <Card className="border-t-4 border-t-[#002d5b]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#002d5b]">
            <AlertTriangle className="h-6 w-6" />
            Déclaration de Dommage
          </CardTitle>
          <CardDescription>
            Dispositif Solidaire DOS-DOMIUM – Cellule F.O.G.E.D.O.M
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="nouvelle" data-testid="tab-nouvelle-declaration">
                <Send className="h-4 w-4 mr-2" />
                Nouvelle Déclaration
              </TabsTrigger>
              <TabsTrigger value="suivi" data-testid="tab-suivi-declarations">
                <Clock className="h-4 w-4 mr-2" />
                Suivi ({mesDeclarations.length})
              </TabsTrigger>
            </TabsList>
            
            {/* TAB: NOUVELLE DÉCLARATION */}
            <TabsContent value="nouvelle" className="space-y-6 mt-6">
              <DeclarationNouvelleForm
                formData={formData}
                souscriptions={souscriptions}
                files={files}
                uploadProgress={uploadProgress}
                loading={loading}
                handleInputChange={handleInputChange}
                handleFileSelect={handleFileSelect}
                removeFile={removeFile}
                handleSubmit={handleSubmit}
              />
            </TabsContent>
            
            {/* TAB: SUIVI DES DÉCLARATIONS */}
            <TabsContent value="suivi" className="mt-6">
              <DeclarationSuiviList
                mesDeclarations={mesDeclarations}
                viewDeclarationDetails={viewDeclarationDetails}
                downloadPDF={downloadPDF}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Modal détails déclaration */}
      <DeclarationDetailsModal
        declaration={selectedDeclaration}
        onClose={() => setSelectedDeclaration(null)}
        downloadPDF={downloadPDF}
      />
      
      <p className="text-center text-gray-400 text-xs">
        Document numérique à valeur probante – Système sécurisé DOMIUM
      </p>
    </div>
  );
}
